import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { venezuelaLocations } from '../utils/venezuelaLocations';
import './Patients.css';

const Patients = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    secondName: '',
    firstLastName: '',
    secondLastName: '',
    sex: 'masculino',
    birthDate: '',
    documentType: 'cedula',
    documentPrefix: 'V',
    documentNumber: '',
    nationality: 'Venezolana',
    state: 'Miranda',
    municipality: '',
    parish: '',
    city: 'Caracas',
    street: '',
    houseNumber: '',
    address: '',
    phone: '',
    email: '',
    paymentMode: 'particular'
  });
  const [availableMunicipalities, setAvailableMunicipalities] = useState([]);
  const [availableParishes, setAvailableParishes] = useState([]);
  const [viewingPatient, setViewingPatient] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      // Add cache-busting timestamp to force fresh data
      const response = await patientService.getAll({
        search: searchTerm,
        _t: Date.now() // Cache-busting parameter
      });
      if (response.success) {
        setPatients(response.data);
      }
    } catch (err) {
      setError('Error al cargar pacientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPatients();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name === 'state') {
      // Update municipalities when state changes
      const municipalities = venezuelaLocations[value]?.municipalities || {};
      setAvailableMunicipalities(Object.keys(municipalities));
      setAvailableParishes([]);
      setFormData(prev => ({
        ...prev,
        state: value,
        municipality: '',
        parish: ''
      }));
    } else if (name === 'municipality') {
      // Update parishes when municipality changes
      const parishes = venezuelaLocations[formData.state]?.municipalities[value] || [];
      setAvailableParishes(parishes);
      setFormData(prev => ({
        ...prev,
        municipality: value,
        parish: ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Initialize municipalities when form opens or patient is loaded for editing
  useEffect(() => {
    if (formData.state) {
      const municipalities = venezuelaLocations[formData.state]?.municipalities || {};
      setAvailableMunicipalities(Object.keys(municipalities));

      if (formData.municipality) {
        const parishes = venezuelaLocations[formData.state]?.municipalities[formData.municipality] || [];
        setAvailableParishes(parishes);
      }
    }
  }, [formData.state, formData.municipality]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({}); // Clear previous errors

    try {
      let response;
      if (editingPatient) {
        response = await patientService.update(editingPatient._id, formData);
        if (response.success) {
          alert('Paciente actualizado exitosamente');
          setEditingPatient(null);
        }
      } else {
        response = await patientService.create(formData);
        if (response.success) {
          alert('Paciente creado exitosamente');
        }
      }
      setShowForm(false);
      fetchPatients();
      resetForm();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error al procesar paciente';

      // Procesar errores de validación del backend
      if (err.response?.data?.errors) {
        const errors = {};
        err.response.data.errors.forEach(error => {
          errors[error.field] = error.message;
        });
        setValidationErrors(errors);

        // Scroll al primer campo con error
        const firstErrorField = Object.keys(errors)[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus();
        }

        alert(`${errorMessage}\n\nPor favor revise los campos marcados en rojo.`);
      } else {
        alert(`Error: ${errorMessage}`);
      }
    }
  };

  const handleEdit = (patient) => {
    setEditingPatient(patient);

    // Extract prefix and number from documentNumber (e.g., "V-18794515" or just "18794515")
    let docPrefix = patient.documentPrefix || 'V';
    let docNumber = patient.documentNumber || '';

    // If documentNumber contains a prefix (V-, E-, etc.), split it
    if (docNumber && docNumber.includes('-')) {
      const parts = docNumber.split('-');
      docPrefix = parts[0];
      docNumber = parts[1];
    }

    setFormData({
      firstName: patient.firstName || '',
      secondName: patient.secondName || '',
      firstLastName: patient.firstLastName || '',
      secondLastName: patient.secondLastName || '',
      sex: patient.sex || 'masculino',
      birthDate: patient.birthDate ? patient.birthDate.split('T')[0] : '',
      documentType: patient.documentType || 'cedula',
      documentPrefix: docPrefix,
      documentNumber: docNumber,
      nationality: patient.nationality || 'Venezolana',
      state: patient.state || 'Miranda',
      municipality: patient.municipality || '',
      parish: patient.parish || '',
      city: patient.city || 'Caracas',
      street: patient.street || '',
      houseNumber: patient.houseNumber || '',
      address: patient.address || '',
      phone: patient.phone || '',
      email: patient.email || '',
      paymentMode: patient.paymentMode || 'particular'
    });
    setShowForm(true);
  };

  const handleDelete = async (patient) => {
    const confirmMessage = `⚠️ ADVERTENCIA: ELIMINACIÓN PERMANENTE ⚠️\n\n` +
      `¿Está completamente seguro que desea ELIMINAR PERMANENTEMENTE al paciente:\n\n` +
      `${patient.firstName} ${patient.firstLastName}\n` +
      `Documento: ${patient.documentNumber}\n\n` +
      `Esta acción:\n` +
      `• ELIMINARÁ todos los datos del paciente de la base de datos\n` +
      `• NO se puede deshacer\n` +
      `• Cumple con las leyes de protección de datos (GDPR/LOPD)\n\n` +
      `¿Desea continuar?`;

    if (window.confirm(confirmMessage)) {
      try {
        // Show loading state
        setLoading(true);

        const response = await patientService.delete(patient._id);
        if (response.success) {
          // Remove patient from local state immediately for instant feedback
          setPatients(prevPatients => prevPatients.filter(p => p._id !== patient._id));

          alert('✓ Paciente eliminado permanentemente de la base de datos');

          // Force refresh from server to ensure data is in sync
          // Add cache-busting timestamp to force fresh data
          await fetchPatients();

          // Optional: Force a complete page reload to clear any browser cache
          // Uncomment the line below if you want a full page refresh
          // window.location.reload();
        } else {
          alert(response.message || 'Error al eliminar paciente');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error al eliminar paciente:', err);
        alert(err.response?.data?.message || 'Error al eliminar paciente. Por favor, intente nuevamente.');
        setLoading(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingPatient(null);
    setShowForm(false);
    resetForm();
  };

  const handleView = (patient) => {
    setViewingPatient(patient);
    setShowViewModal(true);
  };

  const handleCloseView = () => {
    setViewingPatient(null);
    setShowViewModal(false);
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      secondName: '',
      firstLastName: '',
      secondLastName: '',
      sex: 'masculino',
      birthDate: '',
      documentType: 'cedula',
      documentPrefix: 'V',
      documentNumber: '',
      nationality: 'Venezolana',
      state: 'Miranda',
      municipality: '',
      parish: '',
      city: 'Caracas',
      street: '',
      houseNumber: '',
      address: '',
      phone: '',
      email: '',
      paymentMode: 'particular'
    });
    setAvailableMunicipalities([]);
    setAvailableParishes([]);
    setValidationErrors({});
  };

  return (
    <div className="patients-page">
      <div className="container">
        <div className="page-header">
          <h1>Gestión de Pacientes</h1>
          <div className="page-header-actions">
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              ← Volver al Dashboard
            </button>
            <button className="btn btn-primary" onClick={() => {
              if (showForm) {
                handleCancelEdit();
              } else {
                setShowForm(true);
              }
            }}>
              {showForm ? 'Cancelar' : '+ Nuevo Paciente'}
            </button>
          </div>
        </div>

        {showForm && (
          <div className="card form-card">
            <h2>{editingPatient ? 'Editar Paciente' : 'Registrar Nuevo Paciente'}</h2>
            <form onSubmit={handleSubmit} className="patient-form">
              <div className="form-grid">
                <div className="form-group">
                  <label>Primer Nombre *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={validationErrors.firstName ? 'input-error' : ''}
                    required
                  />
                  {validationErrors.firstName && (
                    <span className="error-message">{validationErrors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Segundo Nombre</label>
                  <input
                    type="text"
                    name="secondName"
                    value={formData.secondName}
                    onChange={handleInputChange}
                    className={validationErrors.secondName ? 'input-error' : ''}
                  />
                  {validationErrors.secondName && (
                    <span className="error-message">{validationErrors.secondName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Primer Apellido *</label>
                  <input
                    type="text"
                    name="firstLastName"
                    value={formData.firstLastName}
                    onChange={handleInputChange}
                    className={validationErrors.firstLastName ? 'input-error' : ''}
                    required
                  />
                  {validationErrors.firstLastName && (
                    <span className="error-message">{validationErrors.firstLastName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Segundo Apellido</label>
                  <input
                    type="text"
                    name="secondLastName"
                    value={formData.secondLastName}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label>Sexo *</label>
                  <select name="sex" value={formData.sex} onChange={handleInputChange} required>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Fecha de Nacimiento *</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={formData.birthDate}
                    onChange={handleInputChange}
                    className={validationErrors.birthDate ? 'input-error' : ''}
                    required
                  />
                  {validationErrors.birthDate && (
                    <span className="error-message">{validationErrors.birthDate}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Tipo de Documento *</label>
                  <select
                    name="documentType"
                    value={formData.documentType}
                    onChange={handleInputChange}
                    className={validationErrors.documentType ? 'input-error' : ''}
                    required
                  >
                    <option value="cedula">Cédula de Identidad</option>
                    <option value="pasaporte">Pasaporte</option>
                  </select>
                  {validationErrors.documentType && (
                    <span className="error-message">{validationErrors.documentType}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Nacionalidad del Documento *</label>
                  <select
                    name="documentPrefix"
                    value={formData.documentPrefix}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="V">V (Venezolano)</option>
                    <option value="E">E (Extranjero)</option>
                    <option value="P">P (Pasaporte)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Número de Documento *</label>
                  <input
                    type="text"
                    name="documentNumber"
                    value={formData.documentNumber}
                    onChange={handleInputChange}
                    placeholder="18794515"
                    className={validationErrors.documentNumber ? 'input-error' : ''}
                    required
                  />
                  {validationErrors.documentNumber && (
                    <span className="error-message">{validationErrors.documentNumber}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Teléfono *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={validationErrors.phone ? 'input-error' : ''}
                    required
                  />
                  {validationErrors.phone && (
                    <span className="error-message">{validationErrors.phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={validationErrors.email ? 'input-error' : ''}
                  />
                  {validationErrors.email && (
                    <span className="error-message">{validationErrors.email}</span>
                  )}
                </div>
              </div>

              {/* Sección de Dirección de Residencia */}
              <div className="form-section-divider">
                <h3>Dirección de Residencia</h3>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label>Estado</label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                  >
                    <option value="Distrito Capital">Distrito Capital</option>
                    <option value="Amazonas">Amazonas</option>
                    <option value="Anzoátegui">Anzoátegui</option>
                    <option value="Apure">Apure</option>
                    <option value="Aragua">Aragua</option>
                    <option value="Barinas">Barinas</option>
                    <option value="Bolívar">Bolívar</option>
                    <option value="Carabobo">Carabobo</option>
                    <option value="Cojedes">Cojedes</option>
                    <option value="Delta Amacuro">Delta Amacuro</option>
                    <option value="Falcón">Falcón</option>
                    <option value="Guárico">Guárico</option>
                    <option value="Lara">Lara</option>
                    <option value="Mérida">Mérida</option>
                    <option value="Miranda">Miranda</option>
                    <option value="Monagas">Monagas</option>
                    <option value="Nueva Esparta">Nueva Esparta</option>
                    <option value="Portuguesa">Portuguesa</option>
                    <option value="Sucre">Sucre</option>
                    <option value="Táchira">Táchira</option>
                    <option value="Trujillo">Trujillo</option>
                    <option value="Vargas">Vargas</option>
                    <option value="Yaracuy">Yaracuy</option>
                    <option value="Zulia">Zulia</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Municipio</label>
                  <select
                    name="municipality"
                    value={formData.municipality}
                    onChange={handleInputChange}
                    disabled={availableMunicipalities.length === 0}
                  >
                    <option value="">Seleccione un municipio</option>
                    {availableMunicipalities.map((mun) => (
                      <option key={mun} value={mun}>{mun}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Parroquia</label>
                  <select
                    name="parish"
                    value={formData.parish}
                    onChange={handleInputChange}
                    disabled={availableParishes.length === 0}
                  >
                    <option value="">Seleccione una parroquia</option>
                    {availableParishes.map((par) => (
                      <option key={par} value={par}>{par}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Caracas"
                  />
                </div>

                <div className="form-group">
                  <label>Calle/Avenida</label>
                  <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    placeholder="Av. Principal"
                  />
                </div>

                <div className="form-group">
                  <label>Casa/Edificio/Apto</label>
                  <input
                    type="text"
                    name="houseNumber"
                    value={formData.houseNumber}
                    onChange={handleInputChange}
                    placeholder="Edificio Torre 1, Apto 5-B"
                  />
                </div>

                <div className="form-group full-width">
                  <label>Dirección Completa (Referencia)</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Frente a la plaza, al lado del supermercado"
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary">
                {editingPatient ? 'Actualizar Paciente' : 'Registrar Paciente'}
              </button>
            </form>
          </div>
        )}

        {/* Modal de Visualización de Paciente */}
        {showViewModal && viewingPatient && (
          <div className="modal-overlay" onClick={handleCloseView}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Detalles del Paciente</h2>
                <button className="modal-close" onClick={handleCloseView}>×</button>
              </div>

              <div className="modal-body">
                <div className="patient-detail-section">
                  <h3>Información Personal</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Nombre Completo:</label>
                      <span>{viewingPatient.firstName} {viewingPatient.secondName} {viewingPatient.firstLastName} {viewingPatient.secondLastName}</span>
                    </div>
                    <div className="detail-item">
                      <label>Sexo:</label>
                      <span>{viewingPatient.sex === 'masculino' ? 'Masculino' : viewingPatient.sex === 'femenino' ? 'Femenino' : 'Otro'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Fecha de Nacimiento:</label>
                      <span>{new Date(viewingPatient.birthDate).toLocaleDateString('es-VE')}</span>
                    </div>
                    <div className="detail-item">
                      <label>Edad:</label>
                      <span>{viewingPatient.age} años</span>
                    </div>
                    <div className="detail-item">
                      <label>Nacionalidad:</label>
                      <span>{viewingPatient.nationality}</span>
                    </div>
                  </div>
                </div>

                <div className="patient-detail-section">
                  <h3>Documentación</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Tipo de Documento:</label>
                      <span>{viewingPatient.documentType === 'cedula' ? 'Cédula de Identidad' : 'Pasaporte'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Número de Documento:</label>
                      <span>
                        {viewingPatient.documentNumber && viewingPatient.documentNumber.includes('-')
                          ? viewingPatient.documentNumber
                          : `${viewingPatient.documentPrefix || 'V'}-${viewingPatient.documentNumber}`
                        }
                      </span>
                    </div>
                  </div>
                </div>

                <div className="patient-detail-section">
                  <h3>Contacto</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Teléfono:</label>
                      <span>{viewingPatient.phone}</span>
                    </div>
                    <div className="detail-item">
                      <label>Email:</label>
                      <span>{viewingPatient.email || 'No especificado'}</span>
                    </div>
                  </div>
                </div>

                <div className="patient-detail-section">
                  <h3>📍 Dirección de Residencia</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Estado:</label>
                      <span>{viewingPatient.state}</span>
                    </div>
                    <div className="detail-item">
                      <label>Municipio:</label>
                      <span>{viewingPatient.municipality || 'No especificado'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Parroquia:</label>
                      <span>{viewingPatient.parish || 'No especificado'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Ciudad:</label>
                      <span>{viewingPatient.city}</span>
                    </div>
                    <div className="detail-item">
                      <label>Calle/Avenida:</label>
                      <span>{viewingPatient.street || 'No especificado'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Casa/Edificio/Apto:</label>
                      <span>{viewingPatient.houseNumber || 'No especificado'}</span>
                    </div>
                    <div className="detail-item full-width">
                      <label>Dirección Completa:</label>
                      <span>{viewingPatient.address || 'No especificado'}</span>
                    </div>
                  </div>
                </div>

                <div className="patient-detail-section">
                  <h3>Información Adicional</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Modo de Pago:</label>
                      <span>{viewingPatient.paymentMode === 'particular' ? 'Particular' : viewingPatient.paymentMode === 'seguro' ? 'Seguro' : 'Otro'}</span>
                    </div>
                    <div className="detail-item">
                      <label>Estado del Registro:</label>
                      <span className={viewingPatient.isActive ? 'status-active' : 'status-inactive'}>
                        {viewingPatient.isActive ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Fecha de Registro:</label>
                      <span>{new Date(viewingPatient.createdAt).toLocaleDateString('es-VE')}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                {isAdmin && (
                  <button className="btn btn-primary" onClick={() => {
                    handleCloseView();
                    handleEdit(viewingPatient);
                  }}>
                    Editar Paciente
                  </button>
                )}
                <button className="btn btn-secondary" onClick={handleCloseView}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Buscar por nombre, documento, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="btn btn-primary">Buscar</button>
          </form>
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : error ? (
          <div className="alert alert-error">{error}</div>
        ) : (
          <div className="patients-table card">
            <table>
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Nombre Completo</th>
                  <th>Edad</th>
                  <th>Teléfono</th>
                  <th>Email</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {patients.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">No hay pacientes registrados</td>
                  </tr>
                ) : (
                  patients.map((patient) => (
                    <tr key={patient._id}>
                      <td>{patient.documentNumber}</td>
                      <td>
                        {patient.firstName} {patient.secondName} {patient.firstLastName} {patient.secondLastName}
                      </td>
                      <td>{patient.age} años</td>
                      <td>{patient.phone}</td>
                      <td>{patient.email || '-'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleView(patient)}
                          >
                            Ver
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleEdit(patient)}
                              >
                                Editar
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(patient)}
                              >
                                Eliminar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Patients;
